'use client'

import * as React from 'react'
import { createPortal } from 'react-dom'
import { useTranslations } from 'next-intl'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/icons'
import { Button } from '@/components/ui/button'
import { LanguageToggle } from '@/components/language-toggle'
import { Locale, defaultLocale } from '@/i18n/routing'
import { NavItem } from '@/types/nav'
import Image from 'next/image'
import { Link, usePathname } from '@/i18n/navigation'

interface MobileNavProps {
  items?: NavItem[]
  locale: Locale
}

export function MobileNav({ items, locale }: MobileNavProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const t = useTranslations('MobileNav')
  const linkLocale = locale === defaultLocale ? undefined : locale
  const pathname = usePathname()

  React.useEffect(() => {
    if (!isOpen) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false)
    }
    const desktopViewport = window.matchMedia('(min-width: 768px)')
    const handleViewportChange = () => {
      if (desktopViewport.matches) setIsOpen(false)
    }
    document.addEventListener('keydown', handleKeyDown)
    desktopViewport.addEventListener('change', handleViewportChange)

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleKeyDown)
      desktopViewport.removeEventListener('change', handleViewportChange)
    }
  }, [isOpen])

  const isActive = React.useCallback(
    (href: string) => {
      if (!pathname) return false
      if (!href.startsWith('/')) return false
      if (href === '/') return pathname === '/'
      return pathname === href || pathname.startsWith(`${href}/`)
    },
    [pathname],
  )

  return (
    <>
      {/* 汉堡包菜单按钮 */}
      <Button
        variant="ghost"
        size="sm"
        className="h-9 w-9 p-0 text-white md:hidden"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-controls="mobile-navigation"
      >
        <Icons.menu className="h-5 w-5" />
        <span className="sr-only">{t('toggle')}</span>
      </Button>

      {/* 挂载到页面根部，避免导航栏的背景滤镜将固定侧栏限制在栏内。 */}
      {isOpen &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-40 bg-black/50 md:hidden"
              onClick={() => setIsOpen(false)}
            />
            <div
              id="mobile-navigation"
              className="fixed right-0 top-0 z-50 h-full w-80 max-w-full bg-black/95 shadow-2xl shadow-black/60 md:hidden"
            >
              <div className="flex h-full flex-col">
                {/* 头部 */}
                <div className="flex items-center justify-between border-b border-white/10 p-4">
                  <Image
                    src="/logo.png"
                    alt="logo"
                    width={48}
                    height={48}
                    priority
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-white"
                    onClick={() => setIsOpen(false)}
                  >
                    <Icons.close className="h-4 w-4" />
                    <span className="sr-only">{t('close')}</span>
                  </Button>
                </div>

                {/* 导航菜单 */}
                <nav className="flex-1 overflow-y-auto p-4">
                  <div className="space-y-4">
                    {/* 现有的导航项 */}
                    {items?.map((item) => {
                      if (!item.href) return null
                      const active = isActive(item.href)

                      return (
                        <Link
                          key={item.href}
                          href={item.href}
                          locale={linkLocale}
                          aria-current={active ? 'page' : undefined}
                          className={cn(
                            'block py-2 text-sm font-semibold uppercase tracking-[0.25em] transition-colors',
                            active
                              ? 'text-white'
                              : 'text-white/60 hover:text-amber-200',
                            item.disabled && 'cursor-not-allowed opacity-80',
                          )}
                          onClick={() => setIsOpen(false)}
                        >
                          {item.title}
                        </Link>
                      )
                    })}
                  </div>
                </nav>

                {/* 底部 - 语言切换 */}
                <div className="border-t border-white/10 p-4">
                  <div className="flex items-center justify-center">
                    <LanguageToggle locale={locale} />
                  </div>
                </div>
              </div>
            </div>
          </>,
          document.body,
        )}
    </>
  )
}
